import { useCallback, useEffect, useState } from 'react';
import { Confirm, toast, type Destructive } from '@drbell/ui';
import {
  api, ApiError,
  type Appointment, type AppointmentKind, type Resident,
} from '../api.ts';
import { addDaysToLocalDate, localDateAt } from '@drbell/core';

/**
 * Hospital and clinic appointments — RX-RX-05.
 *
 * The other half of what a care home tracks. A resident on eight medicines
 * also has a nephrology review in six weeks and bloods before it, and a home
 * that misses those is failing them exactly as squarely as a missed dose.
 * Five endpoints existed with no screen anywhere.
 *
 * Dates are wall clock in the patient's timezone, not instants, for the same
 * reason schedules are: "come back on the 5th" is a promise about a date
 * where the patient is standing.
 */
const KINDS: Array<[AppointmentKind, string]> = [
  ['doctor_visit', 'Doctor'],
  ['lab_test', 'Lab test'],
  ['procedure', 'Procedure'],
  ['other', 'Other'],
];

const KIND_LABEL = Object.fromEntries(KINDS) as Record<AppointmentKind, string>;

const STATUS_STYLE: Record<string, string> = {
  attended: 'bg-(--accent-soft) text-(--accent)',
  missed: 'bg-red-500/10 text-red-600',
  cancelled: 'bg-(--panel) text-(--muted)',
};

export function Appointments({ resident, onBack }: {
  resident: Resident;
  onBack: () => void;
}) {
  const [rows, setRows] = useState<Appointment[] | null>(null);
  const [showPast, setShowPast] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Appointment | null>(null);
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState<Destructive | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.get<{ appointments: Appointment[] }>(
        `/api/patients/${resident.id}/appointments${showPast ? '?all=1' : ''}`,
      );
      setRows(d.appointments);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, [resident.id, showPast]);

  useEffect(() => { void load(); }, [load]);

  const resolve = async (a: Appointment, status: 'attended' | 'missed' | 'cancelled') => {
    try {
      await api.post(`/api/patients/${resident.id}/appointments/${a.id}/resolve`, { status });
      toast.success(
        status === 'attended' ? 'Marked as attended'
        : status === 'missed' ? 'Marked as missed'
        : 'Cancelled',
        a.title,
      );
      await load();
    } catch (e) {
      toast.error('Could not update it', e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={onBack}
          className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
        >
          ← Residents
        </button>
        <h2 className="text-lg font-semibold">
          {resident.displayName}
          {resident.locationNote && (
            <span className="ml-2 font-normal text-(--muted)">{resident.locationNote}</span>
          )}
        </h2>
        <button
          onClick={() => setShowPast((v) => !v)}
          aria-pressed={showPast}
          className={`min-h-11 rounded-lg border px-3 text-sm ${
            showPast
              ? 'border-(--accent) bg-(--accent-soft) text-(--accent)'
              : 'border-(--line) hover:border-(--accent)'
          }`}
        >
          Include past
        </button>
        <button
          onClick={() => setAdding(true)}
          className="ml-auto min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white"
        >
          Add an appointment
        </button>
      </div>

      {error && (
        <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
          {error}
        </p>
      )}

      {loading && !rows && <p className="text-(--muted)">Loading…</p>}

      {rows?.length === 0 && (
        <p className="rounded-xl border border-dashed border-(--line) p-8 text-center text-sm text-(--muted)">
          {showPast ? 'Nothing recorded.' : 'Nothing coming up.'}
        </p>
      )}

      <ul className="space-y-2">
        {rows?.map((a) => (
          <li key={a.id} className="rounded-xl border border-(--line) p-4">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-medium tabular-nums">
                {new Date(`${a.localDate}T12:00:00`).toLocaleDateString(undefined, {
                  weekday: 'short', day: 'numeric', month: 'short',
                })}
              </span>
              {/* A time only when there is one. "Come back in March" has no
                  hour, and inventing 00:00 would read as midnight. */}
              {a.localTime && <span className="tabular-nums text-(--muted)">{a.localTime}</span>}
              <span className="font-medium">{a.title}</span>
              <span className="rounded-full border border-(--line) px-2 text-xs text-(--muted)">
                {KIND_LABEL[a.kind]}
              </span>
              {a.status !== 'scheduled' && (
                <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[a.status]}`}>
                  {a.status}
                </span>
              )}
            </div>

            {(a.doctorName || a.locationNote) && (
              <p className="mt-1 text-sm text-(--muted)">
                {[a.doctorName, a.locationNote].filter(Boolean).join(' · ')}
              </p>
            )}
            {a.notes && <p className="mt-1 text-sm">{a.notes}</p>}

            <div className="mt-3 flex flex-wrap gap-2">
              {a.status === 'scheduled' && (
                <>
                  {/* Attended and Missed only once the day has come: before
                      it they are a mistaken tap waiting to happen (care-home
                      walkthrough). Cancelled is offered any time. */}
                  {a.localDate <= localDateAt(Date.now(), resident.timezone) && (<>
                  <button
                    onClick={() => void resolve(a, 'attended')}
                    className="min-h-9 rounded-lg border border-(--line) px-3 text-xs hover:border-(--accent)"
                  >
                    Attended
                  </button>
                  <button
                    onClick={() => void resolve(a, 'missed')}
                    className="min-h-9 rounded-lg border border-(--line) px-3 text-xs hover:border-red-500"
                  >
                    Missed
                  </button>
                  </>)}
                  <button
                    onClick={() => void resolve(a, 'cancelled')}
                    className="min-h-9 rounded-lg border border-(--line) px-3 text-xs hover:border-(--accent)"
                  >
                    Cancelled
                  </button>
                </>
              )}
              <button
                onClick={() => setEditing(a)}
                className="min-h-9 rounded-lg border border-(--line) px-3 text-xs hover:border-(--accent)"
              >
                Edit
              </button>
              <button
                onClick={() =>
                  setPending({
                    title: `Remove "${a.title}"?`,
                    /* Removing and marking cancelled are different things and
                       the difference matters afterwards: one says it never
                       belonged in the record, the other that it was called
                       off. */
                    body: 'It disappears from the record entirely, as though it had never been entered.',
                    reassurance:
                      'If the appointment happened and was called off, mark it Cancelled instead — that keeps the history.',
                    confirmLabel: 'Remove it',
                    onConfirm: async () => {
                      await api.del(`/api/patients/${resident.id}/appointments/${a.id}`);
                      toast.success('Removed');
                      await load();
                    },
                  })
                }
                className="ml-auto min-h-9 rounded-lg border border-(--line) px-3 text-xs hover:border-red-500"
              >
                Remove
              </button>
            </div>
          </li>
        ))}
      </ul>

      {(adding || editing) && (
        <AppointmentDialog
          resident={resident}
          appointment={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={async () => { setAdding(false); setEditing(null); await load(); }}
        />
      )}

      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}
    </section>
  );
}

function AppointmentDialog({ resident, appointment, onClose, onSaved }: {
  resident: Resident;
  appointment: Appointment | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [kind, setKind] = useState<AppointmentKind>(appointment?.kind ?? 'doctor_visit');
  const [title, setTitle] = useState(appointment?.title ?? '');
  const [doctorName, setDoctorName] = useState(appointment?.doctorName ?? '');
  const [locationNote, setLocationNote] = useState(appointment?.locationNote ?? '');
  const [notes, setNotes] = useState(appointment?.notes ?? '');
  const [localDate, setLocalDate] = useState(
    // Tomorrow on the resident's calendar, not UTC's.
    appointment?.localDate ?? addDaysToLocalDate(localDateAt(Date.now(), resident.timezone), 1),
  );
  const [localTime, setLocalTime] = useState(appointment?.localTime ?? '');
  const [remind, setRemind] = useState(String(appointment?.remindDaysBefore?.[0] ?? 1));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    /*
     * What the API takes — defect 142. This sent null for every empty
     * optional field and the reminder as a bare number; creating accepts
     * neither (optional means absent, and reminders are a list of days), so
     * no care home could ever add an appointment, and the refusal showed as
     * "[object Object]". Creating leaves empty fields out; editing sends
     * null, which is how an edit clears one.
     */
    const days = Number(remind);
    const remindDaysBefore = Number.isInteger(days) && days >= 0 ? [days] : [1];
    const optional = (v: string) => v.trim() || undefined;
    try {
      if (appointment) {
        await api.patch(`/api/patients/${resident.id}/appointments/${appointment.id}`, {
          title: title.trim(),
          doctorName: doctorName.trim() || null,
          locationNote: locationNote.trim() || null,
          notes: notes.trim() || null,
          localDate,
          localTime: localTime || null,
          remindDaysBefore,
        });
      } else {
        await api.post(`/api/patients/${resident.id}/appointments`, {
          kind,
          title: title.trim(),
          doctorName: optional(doctorName),
          locationNote: optional(locationNote),
          notes: optional(notes),
          localDate,
          localTime: localTime || null,
          remindDaysBefore,
        });
      }
      toast.success(appointment ? 'Appointment updated' : 'Appointment added', title.trim());
      await onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="appointment-dialog-title">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-(--line) bg-(--bg) p-5">
        <h2 id="appointment-dialog-title" className="text-lg font-semibold">
          {appointment ? 'Edit appointment' : `An appointment for ${resident.displayName}`}
        </h2>

        {!appointment && (
          <div className="mt-4 flex flex-wrap gap-2">
            {KINDS.map(([value, label]) => (
              <button
                key={value}
                onClick={() => setKind(value)}
                aria-pressed={kind === value}
                className={`min-h-11 rounded-lg border px-3 text-sm ${
                  kind === value
                    ? 'border-(--accent) bg-(--accent-soft) text-(--accent)'
                    : 'border-(--line) hover:border-(--accent)'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <label className="mt-3 block">
          <span className="text-xs text-(--muted)">What it is</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Nephrology review"
            autoFocus
            className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
          />
        </label>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-(--muted)">Date</span>
            <input
              type="date"
              value={localDate}
              onChange={(e) => setLocalDate(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
            />
          </label>
          <label className="block">
            <span className="text-xs text-(--muted)">Time — optional</span>
            <input
              type="time"
              value={localTime}
              onChange={(e) => setLocalTime(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
            />
            {/* Said, because leaving it blank is a real answer. */}
            <span className="mt-1 block text-xs text-(--muted)">
              Leave empty if only the day is known.
            </span>
          </label>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-(--muted)">Who — optional</span>
            <input
              value={doctorName}
              onChange={(e) => setDoctorName(e.target.value)}
              placeholder="Dr Iyer"
              className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
            />
          </label>
          <label className="block">
            <span className="text-xs text-(--muted)">Where — optional</span>
            <input
              value={locationNote}
              onChange={(e) => setLocationNote(e.target.value)}
              placeholder="Apollo, OPD 3"
              className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
            />
          </label>
        </div>

        <label className="mt-3 block">
          <span className="text-xs text-(--muted)">Notes — optional</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Fasting bloods first"
            className="mt-1 w-full rounded-lg border border-(--line) bg-(--panel) p-3 text-sm"
          />
        </label>

        <label className="mt-3 block">
          <span className="text-xs text-(--muted)">Remind this many days before</span>
          <input
            value={remind}
            onChange={(e) => setRemind(e.target.value.replace(/\D/g, '').slice(0, 2))}
            inputMode="numeric"
            className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
          />
        </label>

        {error && (
          <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">
            Cancel
          </button>
          <button
            disabled={busy || title.trim().length < 2}
            onClick={() => void save()}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? 'Saving…' : appointment ? 'Save changes' : 'Add it'}
          </button>
        </div>
      </div>
    </div>
  );
}
